import { uuid } from '@coxy/utils'
import { isAnyOf } from '@reduxjs/toolkit'
import {
  CARD_ISSUE_POLL_INTERVAL_MS,
  CARD_ISSUE_TRANSACTIONS_REFRESH_MS,
  CARD_TOPUP_POLL_INTERVAL_MS,
} from '@shared-core/config/polling'
import { actionUserLogout } from '@shared-core/features/auth/actions/global'
import {
  fetchCards,
  fetchTransactions,
  issueCard,
  restoreCardIssue,
  topupCard,
} from '@shared-core/features/backend/actions'
import { apiGetSbpPayment } from '@shared-core/features/backend/api'
import type { CardTemplateId } from '@shared-core/features/backend/models'
import { bumpPaymentSession, getPaymentSessionVersion } from '@shared-core/features/backend/session'
import { cardIssueChanged, issueStateChanged, patchBackendState, storeKey } from '@shared-core/features/backend/slice'
import { addItemToast, TIMEOUT } from '@shared-core/features/notifications'
import { selectedLanguage } from '@shared-core/features/settings'
import { defaultLocale } from '@shared-core/features/settings/configs/localizations'
import { createI18N } from '@shared-core/hooks/use-i18n'
import { navigation, ScreensList } from '@shared-core/navigation'
import { startAppListening } from '@shared-core/store/utils'

import locales from './index.dynamic.i18n.json'

const activeIssueChecks = new Set<string>()
const activeTopupChecks = new Set<string>()
let nextIssueRefreshAt = 0
let issueRefreshSession = -1

// Проверка оплаты продолжается после закрытия QR и перезапуска приложения, но прекращается при logout.
startAppListening({
  matcher: isAnyOf(issueCard.fulfilled, restoreCardIssue.fulfilled, fetchCards.fulfilled),
  effect: async (_action, listener) => {
    const state = listener.getState()[storeKey]
    const invoices = Object.values({
      ...(state.pendingCardIssueInvoice ? { [state.pendingCardIssueInvoice.txId]: state.pendingCardIssueInvoice } : {}),
      ...state.pendingCardIssueInvoices,
    })

    await Promise.all(
      invoices.map(async (invoice) => {
        if (listener.getState()[storeKey].cardIssueShownTxIds?.includes(invoice.txId)) {
          listener.dispatch(issueStateChanged({ txId: invoice.txId, state: 'completed' }))
          return
        }

        const sessionVersion = getPaymentSessionVersion()
        const checkId = `${sessionVersion}:${invoice.txId}`

        if (activeIssueChecks.has(checkId)) return
        activeIssueChecks.add(checkId)

        if (!listener.getState()[storeKey].cardIssue.invoice)
          listener.dispatch(cardIssueChanged({ invoice, isLoading: false, error: null }))
        const isCurrentIssue = () =>
          sessionVersion === getPaymentSessionVersion() &&
          (Boolean(listener.getState()[storeKey].pendingCardIssueInvoices?.[invoice.txId]) ||
            listener.getState()[storeKey].pendingCardIssueInvoice?.txId === invoice.txId) &&
          !listener.getState()[storeKey].cardIssueShownTxIds?.includes(invoice.txId)

        if (issueRefreshSession !== sessionVersion) {
          issueRefreshSession = sessionVersion
          nextIssueRefreshAt = 0
        }

        try {
          while (isCurrentIssue()) {
            try {
              // Готовность определяет только карта со status: active — её опрашиваем на каждом тике,
              // а медленный запрос платежа не должен задерживать открытие шита.
              const shouldRefreshTransactions = Date.now() >= nextIssueRefreshAt

              if (shouldRefreshTransactions) nextIssueRefreshAt = Date.now() + CARD_ISSUE_TRANSACTIONS_REFRESH_MS

              const cards = await listener.dispatch(fetchCards()).unwrap()

              if (!isCurrentIssue()) return

              //Список хранится, чтобы пережить перезапуск.
              if (cards && !listener.getState()[storeKey].cardIssueKnownActiveIds?.[invoice.txId])
                listener.dispatch(
                  patchBackendState({
                    cardIssueKnownActiveIds: {
                      ...listener.getState()[storeKey].cardIssueKnownActiveIds,
                      [invoice.txId]: cards
                        .filter((item) => item.status === 'active' && item.id !== invoice.cardId)
                        .map((item) => item.id),
                    },
                  }),
                )

              const knownActiveIds = listener.getState()[storeKey].cardIssueKnownActiveIds?.[invoice.txId]

              const card = cards?.find(
                (item) =>
                  item.status === 'active' &&
                  (item.id === invoice.cardId ||
                    (item.templateId === invoice.templateId && !knownActiveIds?.includes(item.id))),
              )

              if (card) {
                const template = listener
                  .getState()
                  [storeKey].cardTemplates?.find((item) => item.id === invoice.templateId)
                // Сохраняем показ до навигации, чтобы повторный ответ или запуск не открыл шит снова.
                listener.dispatch(
                  patchBackendState({
                    cardIssueReadyTxId: invoice.txId,
                    cardIssueShownTxIds: Array.from(
                      new Set([...(listener.getState()[storeKey].cardIssueShownTxIds ?? []), invoice.txId]),
                    ),
                  }),
                )
                navigation.resetToHomeWithSheet(ScreensList.FORM_SHEET_CARD_READY, {
                  templateId: invoice.templateId,
                  cardId: card.id,
                  cardName: template?.title ?? card?.name ?? invoice.templateId,
                })
                listener.dispatch(issueStateChanged({ txId: invoice.txId, state: 'completed' }))
                void listener.dispatch(fetchTransactions())
                return
              }

              const [, response] = await Promise.all([
                shouldRefreshTransactions ? listener.dispatch(fetchTransactions()).unwrap() : null,
                apiGetSbpPayment({ routeParams: { id: invoice.txId } }).catch(() => null),
              ])

              if (!isCurrentIssue()) return

              const payment =
                response?.status === 'ok' &&
                response.data?.operation === 'card_issue' &&
                response.data.txId === invoice.txId
                  ? response.data
                  : null

              if (payment && payment.state !== 'completed')
                listener.dispatch(
                  issueStateChanged({
                    txId: invoice.txId,
                    state: payment.state,
                  }),
                )

              if (payment?.state === 'failed' || payment?.state === 'expired') {
                await listener.dispatch(fetchCards())
                await listener.dispatch(fetchTransactions())
                return
              }
            } catch {
              // Временная ошибка сети не отменяет уже созданный счёт.
            }
            await listener.delay(CARD_ISSUE_POLL_INTERVAL_MS)
          }
        } finally {
          activeIssueChecks.delete(checkId)
        }
      }),
    )
  },
})

type ListenerApi = Parameters<Parameters<typeof startAppListening>[0]['effect']>[1]
type TopupInvoice = NonNullable<ReturnType<typeof topupCard.fulfilled>['payload']>

// Закрывает открытый экран с QR и сообщает об успешном пополнении.
function notifyTopupCompleted(listener: ListenerApi, templateId: CardTemplateId | null) {
  if (navigation.getCurrentRouteName() === ScreensList.PAYMENT_LINK)
    navigation.popTo(ScreensList.HOME_START, templateId ? { selectedCardType: templateId } : undefined)

  const state = listener.getState()
  const t = createI18N(selectedLanguage(state), defaultLocale, locales)

  listener.dispatch(
    addItemToast({
      uuid: uuid(5),
      type: 'success',
      text: t('topup-success'),
      timeout: TIMEOUT.DEFAULT,
      override: true,
      isClosable: true,
    }),
  )
}

// Опрос оплаты пополнения до терминального статуса, истечения срока или смены сессии.
async function watchTopupPayment(invoice: TopupInvoice, listener: ListenerApi) {
  const sessionVersion = getPaymentSessionVersion()
  let previousState = invoice.state
  let isSuccessShown = false

  while (
    sessionVersion === getPaymentSessionVersion() &&
    (invoice.expiresAt === null || Date.now() < invoice.expiresAt * 1000)
  ) {
    try {
      const response = await apiGetSbpPayment({ routeParams: { id: invoice.txId } })

      if (sessionVersion !== getPaymentSessionVersion()) return

      const state = response?.data?.state
      const isTerminal = state === 'completed' || state === 'failed' || state === 'expired'

      if (state && (state !== previousState || isTerminal)) {
        const [cards, transactions] = await Promise.all([
          listener.dispatch(fetchCards()).unwrap(),
          listener.dispatch(fetchTransactions()).unwrap(),
        ])

        if (sessionVersion !== getPaymentSessionVersion()) return

        if (state === 'completed' && !isSuccessShown) {
          isSuccessShown = true
          notifyTopupCompleted(
            listener,
            cards?.find((card) => card.id === invoice.cardId)?.templateId ?? invoice.templateId,
          )
        }

        if (isTerminal && cards !== null && transactions !== null) return

        if (!isTerminal) previousState = state
      }
    } catch {
      // Сетевой сбой не отменяет созданное пополнение; продолжаем проверять оплату.
    }

    await listener.delay(CARD_TOPUP_POLL_INTERVAL_MS)
  }

  if (sessionVersion === getPaymentSessionVersion())
    await Promise.all([listener.dispatch(fetchCards()), listener.dispatch(fetchTransactions())])
}

// Следим за оплатой пополнения даже после закрытия QR; повторный topup с тем же txId не запускает второй цикл.
startAppListening({
  actionCreator: topupCard.fulfilled,
  effect: async (action, listener) => {
    const invoice = action.payload

    if (!invoice || activeTopupChecks.has(invoice.txId)) return

    activeTopupChecks.add(invoice.txId)

    try {
      await watchTopupPayment(invoice, listener)
    } finally {
      activeTopupChecks.delete(invoice.txId)
    }
  },
})

startAppListening({
  actionCreator: actionUserLogout,
  effect: () => {
    bumpPaymentSession()
  },
})
